import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { money, formatDateTime, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Receipt } from "lucide-react";

export default function Reportes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("emp") ? "employee" : "company");

  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCo, setSelectedCo] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(params.get("emp") || "");
  const [coReport, setCoReport] = useState(null);
  const [empReport, setEmpReport] = useState(null);

  // Monthly IVA (SAT) report — admin-only
  const now = new Date();
  const [ivaYear, setIvaYear] = useState(now.getFullYear());
  const [ivaMonth, setIvaMonth] = useState(now.getMonth() + 1); // 1..12
  const [ivaCompany, setIvaCompany] = useState("all");
  const [ivaReport, setIvaReport] = useState(null);
  const [ivaLoading, setIvaLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cRes, eRes] = await Promise.all([api.get("/companies"), api.get("/employees")]);
        setCompanies(cRes.data);
        setEmployees(eRes.data);
        if (!selectedCo && cRes.data.length) setSelectedCo(cRes.data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!selectedCo) return;
    (async () => {
      try {
        const { data } = await api.get(`/reports/company/${selectedCo}`);
        setCoReport(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [selectedCo]);

  useEffect(() => {
    if (!selectedEmp) { setEmpReport(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/reports/employee/${selectedEmp}`);
        setEmpReport(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [selectedEmp]);

  useEffect(() => {
    if (!isAdmin || tab !== "iva") return;
    setIvaLoading(true);
    (async () => {
      try {
        const params = { year: ivaYear, month: ivaMonth };
        if (ivaCompany !== "all") params.company_id = ivaCompany;
        const { data } = await api.get("/reports/iva-monthly", { params });
        setIvaReport(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setIvaLoading(false);
      }
    })();
  }, [isAdmin, tab, ivaYear, ivaMonth, ivaCompany]);

  const downloadIvaExcel = async () => {
    try {
      const params = new URLSearchParams({ year: String(ivaYear), month: String(ivaMonth) });
      if (ivaCompany !== "all") params.set("company_id", ivaCompany);
      const res = await api.get(`/export/iva-monthly.xlsx?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NomiDiem-IVA-${ivaYear}-${String(ivaMonth).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="reportes-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#0F172A]">Reportes</h1>
        <p className="text-sm text-[#64748B] mt-1">Análisis por empresa y por empleado.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="company" data-testid="tab-empresa">Por empresa</TabsTrigger>
          <TabsTrigger value="employee" data-testid="tab-empleado">Por empleado</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="iva" data-testid="tab-iva">IVA mensual (SAT)</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="company" className="space-y-5 mt-4">
          <Card className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-xs uppercase tracking-wider text-[#475569]">Empresa</Label>
              <Select value={selectedCo} onValueChange={setSelectedCo}>
                <SelectTrigger data-testid="report-company-select" className="max-w-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>

          {coReport && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MiniStat label="Empleados activos" value={coReport.employees_active} />
                <MiniStat label="Adelantos del mes" value={coReport.advances_month_count} />
                <MiniStat label="Monto total mes" value={money(coReport.total_amount_month)} />
                <MiniStat label="Fees generados mes" value={money(coReport.total_fees_month)} accent />
              </div>

              <Card className="p-0 overflow-hidden">
                <div className="px-4 py-3 border-b border-[#E2E8F0]">
                  <h3 className="font-semibold">Adelantos del mes</h3>
                </div>
                {coReport.advances.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[#64748B]">Sin adelantos este mes</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-wider text-[#64748B]">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Fecha</th>
                        <th className="px-4 py-2 text-right font-medium">Monto</th>
                        <th className="px-4 py-2 text-right font-medium">Fee</th>
                        <th className="px-4 py-2 text-left font-medium">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coReport.advances.map((a) => (
                        <tr key={a.id} className="border-t border-[#E2E8F0]">
                          <td className="px-4 py-2 text-xs text-[#64748B]">{formatDateTime(a.requested_at)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{money(a.amount)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{money(a.fee)}</td>
                          <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="employee" className="space-y-5 mt-4">
          <Card className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-xs uppercase tracking-wider text-[#475569]">Empleado</Label>
              <Select value={selectedEmp} onValueChange={setSelectedEmp}>
                <SelectTrigger data-testid="report-employee-select" className="max-w-sm"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>

          {empReport && (
            <>
              <Card className="p-5">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#64748B]">Empleado</div>
                    <div className="text-xl font-semibold mt-1">{empReport.employee.full_name}</div>
                    <div className="text-xs text-[#64748B] font-mono mt-1">CURP: {empReport.employee.curp}</div>
                    <div className="text-xs text-[#64748B] font-mono">CLABE: {empReport.employee.clabe}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wider text-[#64748B]">Saldo disponible hoy</div>
                    <div className="text-2xl font-bold text-[#147D5C] mt-1 tabular-nums">{money(empReport.employee.saldo?.available)}</div>
                    <div className="text-xs text-[#64748B]">
                      {empReport.employee.saldo?.days_worked}/{empReport.employee.saldo?.period_days} días
                    </div>
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MiniStat label="Total solicitado" value={money(empReport.total_requested)} />
                <MiniStat label="Fees pagados" value={money(empReport.total_fees)} accent />
                <MiniStat label="Liquidados" value={empReport.liquidated_count} />
                <MiniStat label="Pendientes pago" value={empReport.outstanding_count} />
              </div>

              <Card className="p-0 overflow-hidden">
                <div className="px-4 py-3 border-b border-[#E2E8F0]">
                  <h3 className="font-semibold">Historial completo</h3>
                </div>
                {empReport.advances.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[#64748B]">Sin adelantos registrados</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-wider text-[#64748B]">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Solicitado</th>
                        <th className="px-4 py-2 text-right font-medium">Monto</th>
                        <th className="px-4 py-2 text-right font-medium">Fee</th>
                        <th className="px-4 py-2 text-left font-medium">SPEI</th>
                        <th className="px-4 py-2 text-left font-medium">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empReport.advances.map((a) => (
                        <tr key={a.id} className="border-t border-[#E2E8F0]">
                          <td className="px-4 py-2 text-xs text-[#64748B]">{formatDate(a.requested_at)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{money(a.amount)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{money(a.fee)}</td>
                          <td className="px-4 py-2 font-mono text-xs">{a.spei_reference || "—"}</td>
                          <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="iva" className="space-y-5 mt-4">
            <Card className="p-4">
              <div className="flex items-center gap-4 flex-wrap" data-testid="iva-filters">
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Año</Label>
                  <Select value={String(ivaYear)} onValueChange={(v) => setIvaYear(Number(v))}>
                    <SelectTrigger data-testid="iva-year-select" className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {yearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Mes</Label>
                  <Select value={String(ivaMonth)} onValueChange={(v) => setIvaMonth(Number(v))}>
                    <SelectTrigger data-testid="iva-month-select" className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Empresa</Label>
                  <Select value={ivaCompany} onValueChange={setIvaCompany}>
                    <SelectTrigger data-testid="iva-company-select" className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las empresas</SelectItem>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={downloadIvaExcel}
                  data-testid="iva-download-xlsx"
                  className="bg-[#1D9E75] hover:bg-[#147D5C] text-white h-9"
                >
                  Descargar Excel
                </Button>
                <div className="text-xs text-[#64748B] ml-auto">
                  Se incluyen adelantos <strong className="text-[#334155]">TRANSFERIDO / LIQUIDADO</strong> del periodo.
                </div>
              </div>
            </Card>

            {ivaLoading ? (
              <div className="p-8 flex justify-center"><div className="nomi-spin" /></div>
            ) : ivaReport && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <MiniStat label="Adelantos procesados" value={ivaReport.advances_count} />
                  <MiniStat label="Monto total adelantado" value={money(ivaReport.total_amount)} />
                  <MiniStat label="Comisiones (base IVA)" value={money(ivaReport.total_fees)} accent />
                  <MiniStat label="IVA (16%) trasladado" value={money(ivaReport.total_iva)} accent />
                </div>

                <Card className="p-0 overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-[#147D5C]" />
                    <h3 className="font-semibold text-[#0F172A]">Desglose por empresa</h3>
                    <span className="ml-auto text-xs text-[#64748B]">
                      {MONTHS[ivaMonth - 1]} {ivaYear}
                    </span>
                  </div>
                  {ivaReport.by_company.length === 0 ? (
                    <div className="p-10 text-center text-sm text-[#64748B]">
                      Sin adelantos procesados en el periodo seleccionado.
                    </div>
                  ) : (
                    <table className="w-full text-sm" data-testid="iva-breakdown-table">
                      <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-wider text-[#64748B]">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium">Empresa</th>
                          <th className="px-4 py-2 text-left font-medium">RFC</th>
                          <th className="px-4 py-2 text-right font-medium">Adelantos</th>
                          <th className="px-4 py-2 text-right font-medium">Monto adelantado</th>
                          <th className="px-4 py-2 text-right font-medium">Comisiones</th>
                          <th className="px-4 py-2 text-right font-medium">IVA (16%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ivaReport.by_company.map((r) => (
                          <tr key={r.company_id} className="border-t border-[#E2E8F0]">
                            <td className="px-4 py-2 font-medium text-[#0F172A]">{r.company_name}</td>
                            <td className="px-4 py-2 font-mono text-xs text-[#64748B]">{r.company_rfc || "—"}</td>
                            <td className="px-4 py-2 text-right tabular-nums">{r.advances_count}</td>
                            <td className="px-4 py-2 text-right tabular-nums">{money(r.total_amount)}</td>
                            <td className="px-4 py-2 text-right tabular-nums">{money(r.total_fees)}</td>
                            <td className="px-4 py-2 text-right tabular-nums font-semibold text-[#147D5C]">{money(r.total_iva)}</td>
                          </tr>
                        ))}
                        <tr className="bg-[#F8FAFC] border-t-2 border-[#1D9E75]" data-testid="iva-totals-row">
                          <td className="px-4 py-2 font-bold" colSpan="2">TOTAL</td>
                          <td className="px-4 py-2 text-right font-bold tabular-nums">{ivaReport.advances_count}</td>
                          <td className="px-4 py-2 text-right font-bold tabular-nums">{money(ivaReport.total_amount)}</td>
                          <td className="px-4 py-2 text-right font-bold tabular-nums">{money(ivaReport.total_fees)}</td>
                          <td className="px-4 py-2 text-right font-bold tabular-nums text-[#147D5C]">{money(ivaReport.total_iva)}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </Card>

                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                  <strong>Nota fiscal:</strong> Este reporte es informativo para apoyar la declaración mensual de IVA ante el SAT. La emisión de CFDI se maneja fuera del sistema.
                </div>
              </>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="stat-card">
      <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-2 tabular-nums ${accent ? "text-[#147D5C]" : "text-[#0F172A]"}`}>{value}</div>
    </div>
  );
}

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function yearOptions() {
  const y = new Date().getFullYear();
  return [y - 2, y - 1, y, y + 1];
}
